import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Skeleton,
  Chip,
  alpha,
  useTheme,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { WarningAmber as WarningIcon, CheckCircle as CheckIcon, OpenInNew as OpenInNewIcon, ExpandMore as ExpandMoreIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { format } from 'date-fns';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];
const DEFAULT_ROWS_PER_PAGE = 5;

const Panel = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#e8eaed'}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  marginBottom: theme.spacing(1.5),
}));

const statusChipSx = (status) => {
  const v = (status || '').toLowerCase().trim();
  const palette = {
    open: { bg: 'rgba(245, 158, 11, .14)', fg: '#B45309' },
    'pending for review': { bg: 'rgba(59, 130, 246, .14)', fg: '#1D4ED8' },
    'pending for discrepancy': { bg: 'rgba(239, 68, 68, .14)', fg: '#B91C1C' },
    complied: { bg: 'rgba(16, 185, 129, .14)', fg: '#047857' },
    completed: { bg: 'rgba(16, 185, 129, .14)', fg: '#047857' },
    'delayed compliance': { bg: 'rgba(239, 68, 68, .14)', fg: '#B91C1C' },
    default: { bg: 'rgba(17, 24, 39, .08)', fg: '#111827' },
  };
  return palette[v] || palette.default;
};

const formatStatusLabel = (s) => (s ? String(s).replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

const SEARCH_FIELD_OPTIONS_MISSED = [
  { value: 'all', label: 'All fields' },
  { value: 'uniqueIdentifier', label: 'UIN / ID' },
  { value: 'complianceActivity', label: 'Activity' },
  { value: 'location', label: 'Location' },
  { value: 'lawArea', label: 'Law area' },
  { value: 'dueDate', label: 'Due date' },
  { value: 'status', label: 'Status' },
];
const SEARCH_FIELD_OPTIONS_COMPLETED = [
  ...SEARCH_FIELD_OPTIONS_MISSED,
  { value: 'completionDate', label: 'Completed on' },
];

function getSearchableString(row, key) {
  const v = row[key];
  if (v == null) return '';
  if (key === 'dueDate' || key === 'completionDate') {
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '';
      const formatted = format(d, 'd MMM yyyy');
      const iso = d.toISOString().slice(0, 10);
      return `${formatted} ${iso}`;
    } catch {
      return String(v);
    }
  }
  if (Array.isArray(v)) return v.join(' ');
  return String(v);
}

function filterTrackers(rows, searchText, searchField, columnKeys) {
  const q = (searchText || '').trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    if (searchField === 'all') {
      return columnKeys.some((key) => getSearchableString(row, key).toLowerCase().includes(q));
    }
    return getSearchableString(row, searchField).toLowerCase().includes(q);
  });
}

export default function ComplianceOverview() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState({ missed: [], completedWithinDueDate: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [missedPage, setMissedPage] = useState(0);
  const [missedRowsPerPage, setMissedRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [completedPage, setCompletedPage] = useState(0);
  const [completedRowsPerPage, setCompletedRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [missedSearchText, setMissedSearchText] = useState('');
  const [missedSearchField, setMissedSearchField] = useState('all');
  const [completedSearchText, setCompletedSearchText] = useState('');
  const [completedSearchField, setCompletedSearchField] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: res } = await api.get('tracker/compliance-overview');
        setData({ missed: res.missed || [], completedWithinDueDate: res.completedWithinDueDate || [] });
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load compliance overview');
        setData({ missed: [], completedWithinDueDate: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const missedColumnKeys = ['uniqueIdentifier', 'complianceActivity', 'location', 'lawArea', 'dueDate', 'status'];
  const completedColumnKeys = ['uniqueIdentifier', 'complianceActivity', 'location', 'lawArea', 'dueDate', 'completionDate', 'status'];
  const filteredMissed = useMemo(
    () => filterTrackers(data.missed, missedSearchText, missedSearchField, missedColumnKeys),
    [data.missed, missedSearchText, missedSearchField]
  );
  const filteredCompleted = useMemo(
    () => filterTrackers(data.completedWithinDueDate, completedSearchText, completedSearchField, completedColumnKeys),
    [data.completedWithinDueDate, completedSearchText, completedSearchField]
  );
  useEffect(() => {
    setMissedPage(0);
  }, [missedSearchText, missedSearchField]);
  useEffect(() => {
    setCompletedPage(0);
  }, [completedSearchText, completedSearchField]);

  if (loading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width={220} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2, mb: 4 }} />
        <Skeleton variant="text" width={260} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={180} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  const renderTable = (rows, columns, emptyMessage, { page, rowsPerPage, onPageChange, onRowsPerPageChange }) => {
    const from = page * rowsPerPage;
    const to = from + rowsPerPage;
    const paginatedRows = rows.length === 0 ? [] : rows.slice(from, to);
    return (
      <>
    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
      <Table size="medium">
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : 'grey.50' }}>
            {columns.map((col) => (
              <TableCell key={col.key} sx={{ fontWeight: 600, py: 1.5 }} align={col.align || 'left'}>
                {col.label}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedRows.map((row) => {
              const uin = row.uniqueIdentifier;
              const sx = statusChipSx(row.status);
              return (
                <TableRow key={row._id || uin} hover>
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={{ py: 1.5 }}>
                      {col.key === 'status' ? (
                        <Chip size="small" label={formatStatusLabel(row.status)} sx={{ fontWeight: 500, bgcolor: sx.bg, color: sx.fg, border: 'none' }} />
                      ) : col.key === 'dueDate' || col.key === 'completionDate' ? (
                        row[col.key] ? format(new Date(row[col.key]), 'd MMM yyyy') : '—'
                      ) : col.key === 'assignedTo' ? (
                        Array.isArray(row.assignedTo) ? row.assignedTo.filter((e) => e !== 'N/A').join(', ') || '—' : (row.assignedTo || '—')
                      ) : (
                        row[col.key] ?? '—'
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Button
                      size="small"
                      variant="text"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      onClick={() => uin && navigate(`/Edit/${uin}`)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    {rows.length > 0 && (
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage="Rows:"
        sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
      />
    )}
      </>
    );
  };

  const missedColumns = [
    { key: 'uniqueIdentifier', label: 'ID' },
    { key: 'complianceActivity', label: 'Activity' },
    { key: 'location', label: 'Location' },
    { key: 'dueDate', label: 'Due date' },
    { key: 'status', label: 'Status' },
  ];
  const completedColumns = [
    { key: 'uniqueIdentifier', label: 'ID' },
    { key: 'complianceActivity', label: 'Activity' },
    { key: 'location', label: 'Location' },
    { key: 'dueDate', label: 'Due date' },
    { key: 'completionDate', label: 'Completed on' },
    { key: 'status', label: 'Status' },
  ];

  const lastMissedPage = Math.max(0, Math.ceil(filteredMissed.length / missedRowsPerPage) - 1);
  const lastCompletedPage = Math.max(0, Math.ceil(filteredCompleted.length / completedRowsPerPage) - 1);
  const safeMissedPage = Math.min(missedPage, lastMissedPage);
  const safeCompletedPage = Math.min(completedPage, lastCompletedPage);

  const renderSearchBar = (searchText, onSearchTextChange, searchField, onSearchFieldChange, fieldOptions, totalCount, filteredCount) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
      <TextField
        size="small"
        placeholder="Search trackers..."
        value={searchText}
        onChange={(e) => onSearchTextChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 220 }}
      />
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="search-field-label">Search in</InputLabel>
        <Select
          labelId="search-field-label"
          value={searchField}
          label="Search in"
          onChange={(e) => onSearchFieldChange(e.target.value)}
        >
          {fieldOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {searchText.trim() && totalCount !== filteredCount && (
        <Typography variant="body2" color="text.secondary">
          Showing {filteredCount} of {totalCount}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion defaultExpanded={false} sx={{ mb: 1, '&:before': { display: 'none' }, borderRadius: '12px !important', overflow: 'hidden', border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#e8eaed'}` }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: theme.palette.background.paper, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}>
          <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />
          <Typography sx={{ fontWeight: 600 }}>Missed compliances (past due)</Typography>
          <Chip size="small" label={data.missed.length} sx={{ ml: 1 }} />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Trackers whose due date has passed and are not yet completed. Not Applicable excluded.
          </Typography>
          {renderSearchBar(missedSearchText, setMissedSearchText, missedSearchField, setMissedSearchField, SEARCH_FIELD_OPTIONS_MISSED, data.missed.length, filteredMissed.length)}
          {renderTable(filteredMissed, missedColumns, missedSearchText.trim() ? 'No trackers match your search.' : 'No missed compliances.', {
            page: safeMissedPage,
            rowsPerPage: missedRowsPerPage,
            onPageChange: (_, p) => setMissedPage(p),
            onRowsPerPageChange: (e) => { setMissedRowsPerPage(parseInt(e.target.value, 10)); setMissedPage(0); },
          })}
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded={false} sx={{ '&:before': { display: 'none' }, borderRadius: '12px !important', overflow: 'hidden', border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#e8eaed'}` }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: theme.palette.background.paper, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}>
          <CheckIcon sx={{ fontSize: 20, color: 'success.main' }} />
          <Typography sx={{ fontWeight: 600 }}>Completed within due date</Typography>
          <Chip size="small" label={data.completedWithinDueDate.length} sx={{ ml: 1 }} />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Trackers completed on or before their due date. Not Applicable excluded.
          </Typography>
          {renderSearchBar(completedSearchText, setCompletedSearchText, completedSearchField, setCompletedSearchField, SEARCH_FIELD_OPTIONS_COMPLETED, data.completedWithinDueDate.length, filteredCompleted.length)}
          {renderTable(filteredCompleted, completedColumns, completedSearchText.trim() ? 'No trackers match your search.' : 'No trackers completed within due date yet.', {
            page: safeCompletedPage,
            rowsPerPage: completedRowsPerPage,
            onPageChange: (_, p) => setCompletedPage(p),
            onRowsPerPageChange: (e) => { setCompletedRowsPerPage(parseInt(e.target.value, 10)); setCompletedPage(0); },
          })}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
